/* eslint-disable max-lines */
/* eslint-disable no-restricted-syntax */
import EmployeesService from '~/services/employee.service';
import Sound from '~/lib/Sound';
import tickerSoundUrl from '~/assets/sounds/customers/ticker.mp3';
import {
	EXPENSES,
	EMPLOYEES_WORK_STATUS,
} from '~/lib/constants';
import { EmployeeBuilder } from '~/models';
import { inboxActions, loadingActions, employeesSelectors, customerSelectors, uiActions, gameSelectors, turnSelectors } from '~/store';
import { getLocalStore, setLocalStore } from '~/lib/helpers';
// ---- internal imports ----

import * as types from './actionTypes';
// ---- local imports ----

const tickerSound = new Sound(tickerSoundUrl);

function processEmployeesLists(state, hiredEmployeesRecords) {
	let candidates = [];
	let hiredEmployees = [];
	const turnNum = turnSelectors.getTurnNum(state);
	const filteredEmployeesRecord = hiredEmployeesRecords.filter((he) => he.fired_turn === null || he.fired_turn === turnNum )
	const allEmployees = state.employees.allEmployees;
	const currentTurnNum = turnSelectors.getTurnNum(state);

	allEmployees.forEach((employee) => {
		const hiredEmployeeRecord = filteredEmployeesRecord
			.find((record) => (record.employee_id === employee.id));

		if (hiredEmployeeRecord === undefined) {
			candidates.push(employee);
			return;
		}

		if(hiredEmployeeRecord.fired_turn === null) {
			//	the hiredEmployeeId needs to overwrtie employee id
			//	thus i palced it letter
			hiredEmployees.push({ ...employee, ...hiredEmployeeRecord});
		} else if(hiredEmployeeRecord.fired_turn < currentTurnNum) {
			candidates.push(employee);
		}
	});

	return { candidateEmployees: candidates, hiredEmployees };
}

function updateHiredEmployeesOnNewTurn() {
	return async (dispatch, getState) => {
		try {
			let employeeData = processEmployeesLists(
				getState(),
				getState().employees.rawHiredEmployees
			);

			dispatch({
				type: types.EMPLOYEES_UPDATE_HIRED_EMPLOYEES,
				hiredEmployees: employeeData.hiredEmployees,
				candidateEmployees: employeeData.candidateEmployees,
			});
		} catch (error) {
			console.error(error);
		}
	};
}

function hireEmployee(employee) {
	return async (dispatch, getState) => {
		try {
			const gameUserId = gameSelectors.getGameId(getState());
			const adjustedSalary = employeesSelectors.getEmployeeSalary(getState(), employee);
			dispatch(loadingActions.start(types.EMPLOYEES_HIRE));
			const result = await EmployeesService.hire(gameUserId, employee.slug);

			if (!result.isSuccess) {
				dispatch(loadingActions.endAfterNetworkFaliures());
				return false;
			}

			const { hiredEmployees, notifications } = result.data;
			dispatch(loadingActions.end(types.EMPLOYEES_HIRE));
			let employeeData = processEmployeesLists(getState(), hiredEmployees);
			dispatch(inboxActions.handleNewMessage(notifications));
			dispatch(uiActions.addNewExpenseToDisplay(EXPENSES.EMPLOYEE, adjustedSalary));
			dispatch({
				type: types.EMPLOYEES_HIRE,
				rawHiredEmployees: hiredEmployees,
				hiredEmployees: employeeData.hiredEmployees,
				candidateEmployees: employeeData.candidateEmployees,
				employee
			});
			dispatch(automateEmployee(employee.jobType));

			return true;
		} catch (error) {
			console.error(error);
		}
	};
}

function fireEmployee(employee) {
	return async (dispatch, getState) => {
		try {
			const gameUserId = gameSelectors.getGameId(getState());

			dispatch(loadingActions.start(types.EMPLOYEES_FIRE));

			const result = await EmployeesService.fire(gameUserId, employee.id);

			if (!result.isSuccess) {
				dispatch(loadingActions.endAfterNetworkFaliures());
				return false;
			}

			const hiredEmployees = result.data;
			dispatch(loadingActions.end(types.EMPLOYEES_FIRE));
			let employeeData = processEmployeesLists(getState(), hiredEmployees);
			dispatch({
				type: types.EMPLOYEES_FIRE,
				rawHiredEmployees: hiredEmployees,
				hiredEmployees: employeeData.hiredEmployees,
				candidateEmployees: employeeData.candidateEmployees
			});
			return true;
		} catch (error) {
			console.error(error);
		}
	};
}

function getHiredEmployees() {
	return async (dispatch, getState) => {
		try {
			const gameUserId = gameSelectors.getGameId(getState());

			const result =  await EmployeesService.getHiredEmployees(gameUserId);

			if (!result.isSuccess) {
				return;
			}

			const hiredEmployees = result.data;
			let employeeData = processEmployeesLists(getState(), hiredEmployees);

			dispatch({
				type: types.EMPLOYEES_UPDATE_HIRED_EMPLOYEES,
				rawHiredEmployees: hiredEmployees,
				hiredEmployees: employeeData.hiredEmployees,
				candidateEmployees: employeeData.candidateEmployees,
			});
		} catch (error) {
			console.error(error);
		}
	};
}

function getAllEmployees() {
	return async (dispatch) => {
		try {
			const result = await EmployeesService.getAllEmployees();

			if (!result.isSuccess) {
				return;
			}

			const allEmployees = result.data;
			const allEmployeesFormatted = allEmployees.map((employee) => 
				new EmployeeBuilder()
					.withId(employee.id)
					.withSlug(employee.slug)
					.withName(employee.name)
					.withBio(employee.bio)
					.withCalibre(employee.calibre)
					.withSalary(employee.salary)
					.withInitialLevel(employee.initial_level)
					.withJobType(employee.job_type)
					.withJobTitle(employee.job_title)
					.withJobDescription(employee.job_description)
					.build().toPlainObj()
			);
		
			dispatch({ type: types.EMPLOYEES_UPDATE_ALL_EMPLOYEES, value: allEmployeesFormatted });
		} catch (error) {
			console.error(error);
		}
	};
}

function loadAutomatedEmployees() {
	return async (dispatch) => {
		let automatedEmployees = getLocalStore('automatedEmployees');

		if (automatedEmployees === null) {
			automatedEmployees = {};
		}

		dispatch({ type: types.EMPLOYEES_LOAD_AUTOMATED, automatedEmployees });
	};
}

function automateEmployee(jobType) {
	return async (dispatch, getState) => {
		await dispatch({ type: types.EMPLOYEES_ENABLE_AUTO_FUNCTIONING, jobType });

		const allAutomatedEmployees =
			employeesSelectors.getEmployeesFunctioningAutomatically(getState());

		setLocalStore('automatedEmployees', allAutomatedEmployees);
	};
}

function resetEmployeesWorkStatus() {
	return { type: types.EMPLOYEES_RESET_WORK_STATUS };
}

function updateEmployeeWorkStatusInProgress(jobType) {
	return {
		type: types.EMPLOYEES_UPDATE_WORK_STATUS_IN_PROGRESS,
		jobType,
		workStatus: EMPLOYEES_WORK_STATUS.IN_PROGRESS
	};
}

function updateEmployeeWorkStatusDone(jobType) {
	return {
		type: types.EMPLOYEES_UPDATE_WORK_STATUS_DONE,
		jobType,
		workStatus: EMPLOYEES_WORK_STATUS.DONE
	};
}

function updateEmployeesCanStartWorkWithViewingCustomers(canStartWork) {
	return async (dispatch, getState) => {
		dispatch(manageEmployeesWorkInProgressAnimations(canStartWork));

		dispatch({
			type: types.EMPLOYEES_UPDATE_CAN_WORK_WITH_VIEWING_CUSTOMERS,
			canStartWork});
	};
}

function resetCustomerEmployeesWorkCycle(){
	return async (dispatch, getState) => {
		dispatch(updateEmployeesCanStartWorkWithViewingCustomers(false));
		dispatch(resetEmployeesWorkStatus());
	};
}

function manageEmployeesWorkInProgressAnimations(canStartWork){
	return async (dispatch, getState) => {
		const hasNegotiatorHired = employeesSelectors.hasNegotiator(getState());
		const shouldNegotiateCurrentCustomers = customerSelectors
			.canNegotiatorWorkWithViewingCustomers(getState());
		const canStartLorenzoAnimationsAndSound = 
			canStartWork && hasNegotiatorHired && shouldNegotiateCurrentCustomers;
		
		if (canStartLorenzoAnimationsAndSound) {
			startLorenzoWorkInProgressAnimations();
		}
	};
}

function startLorenzoWorkInProgressAnimations() {
	tickerSound.play();
}

export {
	updateHiredEmployeesOnNewTurn,
	hireEmployee,
	fireEmployee,
	getHiredEmployees,
	getAllEmployees,
	loadAutomatedEmployees,
	automateEmployee,
	updateEmployeeWorkStatusInProgress,
	updateEmployeeWorkStatusDone,
	resetCustomerEmployeesWorkCycle,
	updateEmployeesCanStartWorkWithViewingCustomers
};
